import {
  useRef,
  useEffect,
  useState,
} from 'react';
import { useViewer } from '../../../utils/hooks/kinesis-webrtc/useViewer';
import { StreamConfigResponse } from '../../../API/types/Videos';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetRole from '../../../API/hooks/useGetRole';
import useGetStreamConfig from '../../../API/hooks/videos/useGetStreamConfig';
import useGetDeviceErr from '../../../API/hooks/videos/useGetDeviceErr';
import useGetVideoErr from '../../../API/hooks/videos/useGetVideoErr';
import S from './LiveVideoStyles';
import { logger } from '../../../API/services/SharedService';
import { getStreamConfig } from '../../../API/services/VideoService';
import { useStreamTimer, formatDuration } from './useStreamTimer';

// ---------- types ----------

type VideoPlayerProps = {
  streamConfig: StreamConfigResponse | null;
  deviceId: string;
  onMediaUpdate?: (media: any) => void;
  onFirstFrame?: () => void;
  onVideoReady?: (ready: boolean) => void;
  startingTheStream: (state: any) => void;
  isStreamActive: boolean;
};

export type WebRtcPlayerProps = {
  deviceId: string;
  cameraId: number;
  streamVisibilityDuration: number;
  VPNStatus: any;
  isActive: any;
  deviceStatus: boolean;
};

// ---------- inner player (WebRTC peer + video element) ----------

function VideoPlayer({
  streamConfig, deviceId, onMediaUpdate, onFirstFrame, onVideoReady, startingTheStream, isStreamActive,
}: VideoPlayerProps): JSX.Element {
  const startingTheStreamRef = useRef(startingTheStream);
  useEffect(() => { startingTheStreamRef.current = startingTheStream; }, [startingTheStream]);

  const videoRef = useRef<any>();
  const { canPlayVideoAudio } = useGetRole();
  const [videoReady, setVideoReady] = useState(false);

  const config = {
    credentials: {
      accessKeyId: streamConfig?.accessKeyId as string,
      secretAccessKey: streamConfig?.secretAccessKey as string,
      sessionToken: streamConfig?.sessionToken as string,
    },
    channelARN: streamConfig?.channelArn as string,
    region: streamConfig?.region as string,
    clientId: deviceId,
  };
  const {
    signalingClient,
    error,
    peer: { media } = {},
    peerConnection,
  } = useViewer(config);

  console.log(peerConnection?.connectionState, 'peer connection state');

  /* eslint-disable consistent-return */
  useEffect(() => {
    if (!peerConnection) return;

    const interval = setInterval(async () => {
      const stats = await peerConnection.getStats();

      stats.forEach((report: any) => {
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          const packetsLost = report.packetsLost || 0;
          const packetsReceived = report.packetsReceived || 0;
          const total = packetsLost + packetsReceived;
          const lossPercent = total > 0 ? (packetsLost / total) * 100 : 0;

          console.log('📉 Packet Loss %:', lossPercent.toFixed(2));
          console.log('📦 Packets Lost:', packetsLost);
          console.log('📥 Packets Received:', packetsReceived);
          console.log('📊 Jitter:', report.jitter);
        }
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [peerConnection]);

  useEffect(() => {
    if (!peerConnection) return;

    const handleConnectionStateChange = (): void => {
      const state = peerConnection.connectionState;
      if ((state === 'disconnected' || state === 'failed' || state === 'closed') && isStreamActive) {
        startingTheStreamRef.current(state);
      }
    };

    peerConnection.addEventListener('connectionstatechange', handleConnectionStateChange);
    return () => {
      peerConnection.removeEventListener('connectionstatechange', handleConnectionStateChange);
    };
  }, [peerConnection, isStreamActive]);
  /* eslint-enable consistent-return */

  console.log(peerConnection, 'connection state');

  useEffect(() => {
    if (videoRef.current) {
      setVideoReady(false);
      videoRef.current.srcObject = media;
    }
  }, [media, videoRef]);

  useEffect(() => {
    if (onMediaUpdate) onMediaUpdate(media);
  }, [media]);

  if (error) {
    return <p className="video-preview">An error occurred: {error.message}</p>;
  }

  return (
    <div className="video-stage">
      {!videoReady && <p className="video-preview">Connecting to the device...</p>}
      <video
        className="live-video"
        ref={videoRef}
        playsInline
        autoPlay
        muted={!canPlayVideoAudio}
        onCanPlay={() => {
          setVideoReady(true);
          onVideoReady?.(true);
          onFirstFrame?.();
        }}
        style={{ display: videoReady ? 'block' : 'none' }}
      >
        <track kind="captions" src="" label="English" />
      </video>
    </div>
  );
}

// ---------- stream lifecycle (unchanged from original WatchVideo) ----------

function WebRtcPlayer({
  deviceId,
  cameraId,
  streamVisibilityDuration,
  VPNStatus,
  isActive,
  deviceStatus,
}: WebRtcPlayerProps): JSX.Element {
  const fleetId = useGetFleetId();
  const [useStream, setUseStream] = useState(false);
  const [enable, setEnable] = useState(false);
  const {
    data: streamConfig,
    isLoading,
    error: failure,
  } = useGetStreamConfig(deviceId, fleetId, cameraId, VPNStatus, enable);
  const { data: deviceErrData } = useGetDeviceErr(deviceId, enable);
  const { data: videoErrData } = useGetVideoErr(deviceId, enable);

  const [streamData, setStreamData] = useState<StreamConfigResponse | null | undefined>(null);
  const [media, setMedia] = useState<any>();
  const [streamTimeoutEnded, setStreamTimeoutEnded] = useState(false);
  const [retry, setRetry] = useState(0);
  const retryRef = useRef(0);
  const isRetryingRef = useRef(false);
  const [status, setStatus] = useState('');
  const [retryErr, setRetryErr] = useState('');
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [connectKey, setConnectKey] = useState(0);

  const videoStreamIsDisabled = failure && ((failure as any)?.response?.status === 403);
  let streamTimeout: any;
  let streamCheckingInterval: any;
  const mediaRef = useRef(media);
  const streamTimeoutEndedRef = useRef(streamTimeoutEnded);
  const streamTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => { mediaRef.current = media; }, [media]);
  useEffect(() => { streamTimeoutEndedRef.current = streamTimeoutEnded; }, [streamTimeoutEnded]);

  const { state: timerState, startTimer, stopTimer: stopTimerFn } = useStreamTimer(streamVisibilityDuration);

  const stopStream = (): any => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
    stopTimerFn();
    setIsVideoReady(false);
    clearTimeout(streamTimeout);
    clearInterval(streamCheckingInterval);
    setMedia(null);
    setStreamData(null);
    setUseStream(false);
    logger.info('LiveVideo', 'stopStream');
  };

  useEffect(() => {
    if (deviceStatus === false) {
      stopStream();
      setRetryErr('Device is offline. Please try again when device is back online.');
      setRetry(0);
    } else {
      setRetryErr('');
    }
  }, [deviceStatus]);

  const connectingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const CONNECTING_TIMEOUT_MS = 15000;

  const startStreamRef = useRef<(state: any) => void>(() => {});

  useEffect(() => {
    if (connectingTimeoutRef.current) {
      clearTimeout(connectingTimeoutRef.current);
      connectingTimeoutRef.current = null;
    }
    if (useStream && !media?.active) {
      connectingTimeoutRef.current = setTimeout(() => {
        startStreamRef.current('failed');
      }, CONNECTING_TIMEOUT_MS);
    }
    return () => {
      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current);
        connectingTimeoutRef.current = null;
      }
    };
  }, [useStream, media?.active]);

  const MAX_RETRIES = 3;

  const doConnect = async (): Promise<void> => {
    logger.info('LiveVideo', 'doConnect', `fetching stream config ${new Date()}`);
    console.log('[doConnect] args:', {
      deviceId, fleetId, cameraId, VPNStatus,
    });
    try {
      const fresh = await getStreamConfig(deviceId, fleetId, cameraId, VPNStatus);
      console.log('[doConnect] response:', fresh);
      logger.info('LiveVideo', 'doConnect', 'got fresh config, starting stream');
      setStreamData(fresh);
      setConnectKey((k) => k + 1);
      setRetryErr('');
      setUseStream(true);
      setEnable(true);
      isRetryingRef.current = false;
    } catch (err) {
      logger.info('LiveVideo', 'doConnect', `error: ${err}`);
      isRetryingRef.current = false;
      setRetryErr('Unable to connect. Please try again later.');
    }
  };

  const startStream = (state: any): void => {
    setStatus(state);
    if (deviceStatus === false) {
      setRetryErr('Device is offline. Please try again when device is back online.');
      return;
    }
    if (state === 'failed' || state === 'disconnected' || state === 'closed') {
      if (isRetryingRef.current) return;
      if (retryRef.current < MAX_RETRIES) {
        isRetryingRef.current = true;
        retryRef.current += 1;
        setRetry(retryRef.current);
        setRetryErr(`Connection lost. Retrying... (${retryRef.current}/${MAX_RETRIES})`);
        stopStream();
        setStreamTimeoutEnded(false);
        setTimeout(() => { doConnect(); }, 3000);
      } else {
        setRetryErr('Unable to reconnect after multiple attempts. Please try again later.');
      }
    } else {
      logger.info('LiveVideo', 'startStream', `Trying to start stream ${new Date()}`);
      setRetryErr('');
      stopStream();
      setStreamTimeoutEnded(false);
      retryRef.current = 0;
      setRetry(0);
      setEnable(true);
      doConnect();
    }
  };

  startStreamRef.current = startStream;

  const useStreamRef = useRef(useStream);
  useEffect(() => { useStreamRef.current = useStream; }, [useStream]);

  useEffect(() => {
    if (videoErrData?.videoErr && useStreamRef.current) {
      startStream('failed');
    }
  }, [videoErrData?.seqNum]);

  const streamVisibilityDurationRef = useRef(streamVisibilityDuration);
  useEffect(() => { streamVisibilityDurationRef.current = streamVisibilityDuration; }, [streamVisibilityDuration]);

  const handleFirstFrame = (): void => {
    retryRef.current = 0;
    setRetry(0);
    setRetryErr('');
    startTimer();

    if (streamTimeoutRef.current) clearTimeout(streamTimeoutRef.current);
    streamTimeoutRef.current = setTimeout(() => {
      stopStream();
      setEnable(false);
      setStreamTimeoutEnded(true);
    }, streamVisibilityDurationRef.current);
  };

  useEffect(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = setTimeout(() => {
        stopStream();
        setStreamTimeoutEnded(true);
      }, streamVisibilityDuration);
    }
  }, [streamVisibilityDuration]);

  console.log('configDetails', streamVisibilityDuration, videoStreamIsDisabled);

  const placeholder = (message: string, actions?: JSX.Element): JSX.Element => (
    <S.VideoContainer>
      <div className="video-stage-placeholder">
        <span>{message}</span>
        {actions}
      </div>
    </S.VideoContainer>
  );

  if (videoStreamIsDisabled) {
    return placeholder('Error: Video Streaming is disabled!');
  }
  if (deviceStatus === false) {
    return placeholder('Device is offline. Please try again when device is back online.');
  }
  if (streamTimeoutEnded && !isLoading) {
    return placeholder(
      'Stream duration limit reached. Please try again or change stream duration.',
      <button type="button" onClick={() => { setStreamTimeoutEnded(false); }}>Start new stream</button>,
    );
  }
  if (retryErr !== '') {
    return placeholder(retryErr);
  }
  if (!useStream && VPNStatus) {
    return placeholder(
      'Test the channel availability before establishing a connection.',
      <button type="button" onClick={() => startStream(null)}>Check availability</button>,
    );
  }

  if ((isLoading || !streamData) && VPNStatus) {
    return placeholder('Preparing to connect...');
  }
  if (!streamData) {
    return placeholder(
      'Press "Start" to request live video',
      <button type="button" onClick={startStream}>Start stream</button>,
    );
  }
  return (
    <S.VideoContainer>
      <VideoPlayer
        key={connectKey}
        streamConfig={streamData}
        deviceId={deviceId}
        onMediaUpdate={setMedia}
        onFirstFrame={handleFirstFrame}
        onVideoReady={setIsVideoReady}
        startingTheStream={(state: any) => { startStream(state); }}
        isStreamActive={useStream}
      />
      {isVideoReady && timerState.elapsed > 0 && (
        <div className="stream-timer">
          <span>Stream duration: {formatDuration(timerState.elapsed)}</span>
          {timerState.remaining !== null && (
            <span>Remaining: {formatDuration(timerState.remaining)}</span>
          )}
        </div>
      )}
    </S.VideoContainer>
  );
}

export default WebRtcPlayer;
